import React from 'react'
import { AgTechFormControlUpdate } from '@agtech/core/forms/AgTechFormControls';
import { AgTechSelectionProps } from '@agtech/web/components/Forms/Controls/Selection/AgTechCoreSelectionControlData';
import { AgTechSelectionListHeader, AgTechSelectionListBody } from '@agtech/web/components/Forms/Controls/Selection/AgTechSelectionPopUp';
import AgTechTitleDetailHeader from '@agtech/web/components/Headers/AgTechTitleDetailHeader';
import ContactDialog from '../../dialogs/ContactDialog';
import { useAgTechQuery } from '@agtech/core/data/queries/AgTechDataQueries';
import { AgTechContact } from '@agtech/data/contacts/ContactData';
import { ContactDataGetAllContactsQuery } from '@agtech/data/contacts/ContactDataQueries';
import AgTechFormLookup, { AgTechFormLookupItemProps, AgTechFormLookupProps } from '../../../../components/Forms/Controls/Lookups/AgTechFormLookup';

export type ContactSelectionControlProps<TFormEntity> = {
    formEntity: TFormEntity,
    control: {
        caption?: string,
        isRequired?: boolean,
        width?: number
    },
    contacts: {
        getSelectedContactId: (formEntity: TFormEntity) => number | undefined,
        onContactSelected: AgTechFormControlUpdate<TFormEntity, AgTechContact | undefined>,
        filterContacts?: (contact: AgTechContact) => boolean,
        customItems?: (items: AgTechFormLookupItemProps<AgTechContact>) => AgTechFormLookupItemProps<AgTechContact>
    },
    popup?: {
        height?: number,
        width?: number
    }
}

const ContactSelectionControl = <TFormEntity, >(props: ContactSelectionControlProps<TFormEntity>) => {
    let contactSelectionProps = useContactSelectionFormControlProps(props);
    
    return (
        <AgTechFormLookup {...contactSelectionProps} />
    )
}

export const useContactSelectionFormControlProps = <TFormEntity, >(
    props: ContactSelectionControlProps<TFormEntity>
): AgTechFormLookupProps<TFormEntity, AgTechContact> => {
    const contactsQuery = useAgTechQuery({
        query: ContactDataGetAllContactsQuery,
        projection: data => Object.values(data)
    });

    const controlProps: AgTechFormLookupProps<TFormEntity, AgTechContact> = {
        entityType: props.formEntity,
        control: {
            caption: props.control.caption ?? "Contact",
            isRequired: props.control.isRequired,
            width: props.control.width
        },
        selection: {
            selectionControlTitle: 'Contacts',
            data: {
                itemQuery: contactsQuery,
                selectedItemId: props.contacts.getSelectedContactId(props.formEntity)?.toString(),
                searchItems: (item, filter) => (item.firstName + ' ' + item.lastName).toLowerCase().includes(filter.toLowerCase()),
                filterItems: props.contacts.filterContacts
            },
            items: {
                itemText: c => c.firstName + ' ' + c.lastName,
                itemHeight: 60,
                itemRendering: e => (
                    <AgTechTitleDetailHeader
                        title={e.firstName + ' ' + e.lastName}
                        detail={e.ranchName}
                        titleFontSize='font-size-small'
                        detailFontSize='font-size-very-small'
                        detailClasses='mt-1-half'
                    />
                ),
                itemCreationConfig: {
                    creationButtonText: 'New Contact',
                    creationButtonAction: e => {
                        return new Promise<AgTechContact>((res, fail) => {
                            e.dialogs.openDialog({
                                dialog: () => (
                                    <ContactDialog onContactSubmitted={async createdContact => {
                                        res(createdContact);
                                    }} />
                                ),
                            });
                        });
                    },
                    noRecordsMessage: 'No contacts were found in the system',
                    noRecordsPrompt: 'To create one, click the button below'
                },
            },
            behavior: {
                onItemSelected: async (entity, val) => {
                    return props.contacts.onContactSelected(entity, val);
                }
            },
            popup: {
                height: 240,
                width: 420
            }
        }
    };

    if (props.contacts.customItems) {
        controlProps.selection.items = props.contacts.customItems(controlProps.selection.items);
    }

    return controlProps;
}

export type ContactSelectionProps = {
    selectedContactId?: number | undefined,
    onContactSelected: (selectedContact: AgTechContact) => Promise<any>,
    filterContacts?: (contact: AgTechContact) => boolean
}

export const useContactSelectionProps = (
    props: ContactSelectionProps
): AgTechSelectionProps<AgTechContact> => {
    const contactsQuery = useAgTechQuery({
        query: ContactDataGetAllContactsQuery,
        projection: data => Object.values(data)
    });

    return {
        items: {
            itemsQuery: contactsQuery,
            getSelectedItemId: () => props.selectedContactId?.toString(),
            onItemSelected: props.onContactSelected,
            searchItems: (contact, filter) => {
                let includeContact = (contact.firstName + ' ' + contact.lastName).includes(filter);
                
                if (props.filterContacts && includeContact) {
                    includeContact = props.filterContacts(contact);
                }

                return includeContact;
            }
        },
        content: {
            header: headerProps => (
                <AgTechSelectionListHeader
                    {...headerProps}
                    title='Contacts'
                />
            ),
            body: bodyProps => (
                <AgTechSelectionListBody
                    {...bodyProps}
                    itemHeight={60} 
                    item={e => (
                        <AgTechTitleDetailHeader
                            title={e.firstName + ' ' + e.lastName}
                            detail={e.ranchName}
                            titleFontSize='font-size-small'
                            detailFontSize='font-size-very-small'
                            detailClasses='mt-1-half'
                        />
                    )}
                />
            ),
            height: 260,
            width: 400
        }
    }
}

export default ContactSelectionControl